import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';
import { useAdminConfig } from '@/hooks/useAdminConfig';

const PAGE_SIZE = 10;

const AdminReviewQueuePage = () => {
  const { config } = useAdminConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  // (audit 2026-06-11) — Filters dropdown + Claim Review row button were
  // both inert. State below drives the in-memory filter; claimReview() POSTs
  // to the backend.
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [claiming, setClaiming] = useState<string | number | null>(null);

  const loadData = async () => {
    try {
      const response = await apiService.request('/admin/review-queue', 'GET');
      if (response.success && response.data) {
        setFetchedData(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => { loadData(); }, []);

  const claimReview = async (item: any) => {
    if (!item?.id) return;
    setClaiming(item.id);
    try {
      const res: any = await apiService.request(`/admin/review-queue/${item.id}/claim`, 'POST', {});
      if (res?.success) {
        alert('Review claimed — assigned to you');
        await loadData();
      } else {
        alert('Error: ' + (res?.message || 'Failed to claim review'));
      }
    } catch (e: any) {
      alert('Failed: ' + (e?.message || 'Network error'));
    } finally {
      setClaiming(null);
    }
  };

  const handleExportQueue = () => {
    const rows = reviewQueue.length > 0 ? reviewQueue : fetchedData;
    if (!rows.length) { alert('Nothing to export.'); return; }
    const headers = Array.from(rows.reduce((s: Set<string>, r: any) => {
      Object.keys(r || {}).forEach((k) => s.add(k));
      return s;
    }, new Set<string>()));
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(','), ...rows.map((r: any) => headers.map((h) => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `review-queue-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reviewQueue = fetchedData.map((d: any) => {
    const entityName = d.provider_name || d.entity_name || d.company_name || '—';
    const initials = entityName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '??';
    const submittedAt = d.submitted_at || d.created_at;
    let slaStatus = 'HEALTHY';
    if (submittedAt) {
      const hours = (Date.now() - new Date(submittedAt).getTime()) / 3600000;
      if (hours > config.sla.breachedHours) slaStatus = 'SLA BREACHED';
      else if (hours > config.sla.criticalHours) slaStatus = 'SLA CRITICAL';
      else if (hours > config.sla.normalHours) slaStatus = 'NORMAL';
    }
    return {
      id: d.id,
      entity: entityName,
      entityId: `ID: ${d.provider_id || d.entity_id || d.id}`,
      documentType: (d.document_type || d.doc_type || 'DOCUMENT').toUpperCase(),
      status: (d.status || 'PENDING').toUpperCase(),
      claimedByName: d.claimed_by_name || null,
      slaTimer: submittedAt ? new Date(submittedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—',
      slaStatus,
      avatar: initials
    };
  });

  const filteredQueue = statusFilter === 'all'
    ? reviewQueue
    : reviewQueue.filter((r) => r.status.toUpperCase() === statusFilter.toUpperCase());

  const totalPages = Math.max(1, Math.ceil(filteredQueue.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filteredQueue.length);
  const pagedQueue = filteredQueue.slice(pageStart, pageEnd);

  const priorityFlags = reviewQueue.filter(r => r.slaStatus === 'SLA CRITICAL' || r.slaStatus === 'SLA BREACHED').length;

  const avgWaitTime = (() => {
    const withDates = fetchedData.filter((d: any) => d.submitted_at || d.created_at);
    if (withDates.length === 0) return '0h';
    const totalHrs = withDates.reduce((sum: number, d: any) => {
      const t = d.submitted_at || d.created_at;
      return sum + (Date.now() - new Date(t).getTime()) / 3600000;
    }, 0);
    const avg = totalHrs / withDates.length;
    return avg < 1 ? `${Math.round(avg * 60)}m` : `${avg.toFixed(1)}h`;
  })();

  const metrics = [
    {
      label: 'PENDING TOTAL',
      value: String(reviewQueue.length),
      change: 'Documents awaiting review',
      icon: '📋'
    },
    {
      label: 'AVG. WAIT TIME',
      value: avgWaitTime,
      change: '✓ Within SLA targets',
      icon: '⏱️'
    },
    {
      label: 'PRIORITY FLAGS',
      value: String(priorityFlags),
      change: priorityFlags > 0 ? '⚠ Needs immediate attention' : '✓ All within SLA',
      icon: '🚩'
    }
  ];

  const activeReviewers = {
    value: '—',
    subtitle: 'Live reviewer count not available'
  };

  const getSLABadge = (status: string) => {
    const config: Record<string, { bg: string; text: string }> = {
      'SLA CRITICAL': { bg: 'bg-red-100', text: 'text-red-700' },
      HEALTHY: { bg: 'bg-green-100', text: 'text-green-700' },
      NORMAL: { bg: 'bg-blue-100', text: 'text-blue-700' },
      'SLA BREACHED': { bg: 'bg-red-100', text: 'text-red-700' }
    };
    const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    return <Badge className={`${c.bg} ${c.text} border-0`}>{status}</Badge>;
  };

  return (
    <>
      <Helmet>
        <title>Review Queue - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="mb-2 text-sm text-gray-600">
                <span>COMPLIANCE</span>
                <span className="mx-2">{'>'}</span>
                <span className="text-gray-900 font-semibold">REVIEW QUEUE</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-1">Review Queue</h1>
                  <p className="text-gray-600">Documents awaiting platform verification</p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 text-sm bg-white"
                  >
                    <option value="all">Filters: All Statuses</option>
                    <option value="Pending">Filters: Pending</option>
                    <option value="Claimed">Filters: Claimed</option>
                  </select>
                  <button
                    onClick={handleExportQueue}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="max-w-7xl mx-auto px-8 py-8">
            <div className="grid grid-cols-4 gap-6 mb-8">
              {metrics.map((metric, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">{metric.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mb-2">{metric.value}</p>
                    <p className="text-xs text-gray-600">{metric.change}</p>
                  </CardContent>
                </Card>
              ))}

              {/* Active Reviewers Card */}
              <Card className="bg-gradient-to-br from-teal-600 to-teal-700 text-white border-0">
                <CardContent className="pt-6">
                  <p className="text-xs font-bold text-teal-100 uppercase tracking-widest mb-2">ACTIVE REVIEWERS</p>
                  <p className="text-3xl font-bold mb-2">{activeReviewers.value}</p>
                  <p className="text-sm text-teal-100">{activeReviewers.subtitle}</p>
                </CardContent>
              </Card>
            </div>

            {/* Review Queue Table */}
            <Card>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Entity / Provider</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Document Type</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Sla Timer</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedQueue.length === 0 && (
                        <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">No items in review queue.</td></tr>
                      )}
                      {pagedQueue.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold text-sm">{item.avatar}</div>
                              <div>
                                <p className="font-bold text-gray-900">{item.entity}</p>
                                <p className="text-xs text-gray-600">{item.entityId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className="bg-gray-200 text-gray-700 border-0 text-xs">{item.documentType}</Badge>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={item.status === 'CLAIMED' ? 'bg-amber-100 text-amber-700 border-0' : 'bg-blue-100 text-blue-700 border-0'}>● {item.status}</Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-gray-700">{item.slaTimer}</p>
                              {getSLABadge(item.slaStatus)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {item.status === 'CLAIMED' ? (
                              <span className="text-xs text-gray-600 font-medium">Claimed by {item.claimedByName || 'reviewer'}</span>
                            ) : (
                              <button
                                onClick={() => claimReview(item)}
                                disabled={claiming === item.id}
                                className="px-4 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm"
                              >
                                {claiming === item.id ? 'Claiming…' : 'Claim Review'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    {filteredQueue.length === 0
                      ? 'No matching entries'
                      : `Showing ${pageStart + 1} to ${pageEnd} of ${filteredQueue.length} entries`}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                      <button
                        key={n}
                        onClick={() => setCurrentPage(n)}
                        className={`w-8 h-8 text-sm font-bold rounded ${
                          n === currentPage
                            ? 'bg-teal-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminReviewQueuePage;
